import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { type World } from '@dimforge/rapier3d-compat';
import { seededRandom } from '@/utils/random';
import { isMeshObject } from '@/utils/mesh';
import { QualityMode } from '@/utils/quality';
import { createImpostorMaterial, type ImpostorMeta } from '@/shaders/impostor';

export type TreePlanterOptions = {
  groundMeshes?: THREE.Object3D | THREE.Object3D[];
  scene: THREE.Group;
  worldSize: number;
  qualityLevel?: QualityMode;
  /**
   * Always draw the cheapest LOD — the billboard — whatever the distance.
   *
   * For a headset, where a course is already the heaviest thing this engine
   * renders and every instance is drawn twice. Trees on a golf course are
   * scenery you look past, not at, so the geometry LODs buy very little for
   * what they cost.
   */
  billboardOnly?: boolean;
  /**
   * Bisect a "no trees on the headset" report, which is otherwise very hard to
   * reason about from a desk: the device runs the WebGL backend (FuseRenderer
   * forces it whenever xr is on) while a desktop preview runs WebGPU, so the
   * two do not even share a code path.
   *
   * Forces every impostor quad visible — no distance test, no frustum cull —
   * and draws it as flat magenta with no texture and no alpha cutout. That
   * splits the problem cleanly in one look:
   *
   * - Magenta quads appear → geometry, instancing and placement are all fine,
   *   and the fault is in the atlas sample or the alpha cutout.
   * - Still nothing → the quads are not being drawn at all, and the atlas is
   *   irrelevant. Look at instancing and the LOD/visibility bookkeeping.
   */
  debugImpostors?: boolean;
  /**
   * Baked course lightmap. Trees multiply it in at their trunk position so
   * they sit in the same light as the ground they stand on — see
   * ImpostorMaterialOptions.lightmap.
   */
  lightmap?: THREE.Texture;
  /** Course extent the lightmap spans, in metres. */
  lightmapWorldSize?: number;
  /**
   * Hide trees closer than this to the player, in metres.
   *
   * A course page re-poses so the lie lands on the player's mat, and if the lie
   * is in the trees a trunk is standing *in the room* — you are inside the
   * canopy looking out through it, and the ball starts inside a collider. There
   * is no camera distance at which that looks right, so the nearest ones simply
   * do not exist.
   *
   * Note this is NOT `TreeGroup.minDistance`, which is planting spacing.
   */
  nearCull?: number;
};

/**
 * A tree's trunk, for collision. Not a mesh: the ball's terrain lookup is a
 * downward raycast, so putting trunks in that BVH would make each tree a
 * cylindrical mesa the ball lands on top of. These are tested separately, as
 * upright cylinders against the flight path.
 */
export type TreeTrunk = { x: number; z: number; radius: number; top: number; base: number };

/**
 * Slack on the frustum cull, as tan(angle) — roughly 20°.
 *
 * The LOD scan runs at most every 5–10 frames, so anything culled tight to the
 * frustum edge stays hidden for that long after it should have appeared. This
 * is the arc a head can cover in the meantime.
 */
const CULL_ANGLE_PAD = 0.36;

export type TreeGroup = {
  meshGroup: THREE.Group;
  scaleRange: {
    min: number,
    max: number
  },
  density: number,
  minDistance?: number,
  colors: number[],
  collider?: {
    radius: number,
    height: number
  },
  // collider?: boolean,
  lodDistances: number[],
  maxDistance?: number,
};

type BatchEntry = {
  mesh: THREE.BatchedMesh;
  lodGeometryIds: number[];   // index = LOD level; -1 = no geometry at that level
  instanceIds: number[];      // index = planted tree
};

type ImpostorEntry = {
  mesh: THREE.Mesh;
  posScale: THREE.InstancedBufferAttribute; // xyz = quad center (world), w = 0 when hidden
  realScale: Float32Array;                  // per-tree scale to restore on show
  level: number;                            // LOD level the impostor represents
  /** Quad half-extent at scale 1 — the canopy radius, for culling and trunks. */
  radius: number;
  /** Viewer in treeGroup-local space; fed every frame (see createImpostorMaterial). */
  camLocal: { value: THREE.Vector3 };
};

type LODEntry = {
  batches: BatchEntry[];
  impostor?: ImpostorEntry;
  allMatrices: THREE.Matrix4[];
  positions: Float32Array;    // XZ per planted tree
  cullRadius: number;         // approx tree bounding radius for frustum test
  lodDistances: number[];
  maxDistance: number;
  currentLevel: Uint8Array;   // per planted tree
};

class SpatialHash2D {
  private cellSize: number;
  private cells = new Map<string, { x: number; z: number }[]>();

  constructor(cellSize: number) {
    this.cellSize = cellSize;
  }

  private key(x: number, z: number): string {
    return `${Math.floor(x / this.cellSize)},${Math.floor(z / this.cellSize)}`;
  }

  insert(x: number, z: number) {
    const k = this.key(x, z);
    if (!this.cells.has(k)) this.cells.set(k, []);
    this.cells.get(k)!.push({ x, z });
  }

  hasNeighborWithin(x: number, z: number, minDist: number): boolean {
    const r = Math.ceil(minDist / this.cellSize);
    const cx = Math.floor(x / this.cellSize);
    const cz = Math.floor(z / this.cellSize);
    const dSq = minDist * minDist;

    for (let dx = -r; dx <= r; dx++) {
      for (let dz = -r; dz <= r; dz++) {
        const pts = this.cells.get(`${cx + dx},${cz + dz}`);
        if (!pts) continue;
        for (const p of pts) {
          if ((p.x - x) ** 2 + (p.z - z) ** 2 < dSq) return true;
        }
      }
    }
    return false;
  }
}

export class TreePlanter {
  scene: THREE.Group;
  worldSize: number;
  billboardOnly = false;
  debugImpostors = false;
  lightmap?: THREE.Texture;
  lightmapWorldSize?: number;
  /** See TreePlanterOptions.nearCull. */
  nearCull = 0;
  groundMeshes: THREE.Object3D | THREE.Object3D[];
  qualityLevel?: QualityMode;
  treeGroup: THREE.Group;
  #raycaster: THREE.Raycaster;
  lodEntries: LODEntry[] = [];
  #init: boolean = false;
  #lastCamX = 0;
  #lastCamZ = 0;  
  #lastCamDir = new THREE.Vector3();
  #tmpDir = new THREE.Vector3();
  #frameNum = 0;
  #framesSinceLOD = 0;
  // Scratch for #updateLODs, which runs over every planted tree — allocating
  // these per call handed the GC a few thousand objects a second on a headset.
  #projScreen = new THREE.Matrix4();
  #frustum = new THREE.Frustum();
  #sphere = new THREE.Sphere();

  constructor(options: TreePlanterOptions) {
    const { scene, worldSize, groundMeshes } = options;
    this.scene = scene;
    this.worldSize = worldSize;
    this.billboardOnly = !!options.billboardOnly;
    this.debugImpostors = !!options.debugImpostors;
    this.lightmap = options.lightmap;
    this.lightmapWorldSize = options.lightmapWorldSize;
    this.nearCull = options.nearCull ?? 0;
    this.qualityLevel = options.qualityLevel;

    // Normalise groundMeshes to an array
    this.groundMeshes = groundMeshes
      ? (Array.isArray(groundMeshes) ? groundMeshes : [groundMeshes])
      : [];

    // Three.js raycaster used when RAPIER isn't available (or always for Y)
    this.#raycaster = new THREE.Raycaster();
    // this.#raycaster.firstHitOnly = true; // requires three-mesh-bvh or r152+

    this.treeGroup = new THREE.Group();
    this.scene.add(this.treeGroup);
  }

  clear() {
    this.scene.remove(this.treeGroup);
    this.treeGroup = new THREE.Group();
    // this.lods = [];
    this.scene.add(this.treeGroup);
  }

  #getGroundY(x: number, z: number) {
    const originY = 200;

    // if (this.physicsEnabled) {
    //   const ray = new this.rapier!.Ray(
    //     { x, y: originY, z },
    //     { x: 0, y: -1, z: 0 }
    //   );
    //   const hit = this.world!.castRay(ray, 500, true);
    //   if (hit == null) {
    //     console.log('No ground hit...');
    //     return null;
    //   }
    //   return originY - hit.timeOfImpact;
    // }

    // Three.js fallback
    if (!this.groundMeshes || Array.isArray(this.groundMeshes) && this.groundMeshes?.length === 0) return 0; // no ground info, plant at y=0

    this.#raycaster.set(
      new THREE.Vector3(x, originY, z),
      new THREE.Vector3(0, -1, 0)
    );
    const hits = this.#raycaster.intersectObjects(this.groundMeshes as THREE.Object3D[], true);
    if (hits.length === 0) return null;
    return hits[0].point.y;
  }
  // #getGroundY(x: number, z: number) {
  //   if (!this.groundMeshes || (Array.isArray(this.groundMeshes) && this.groundMeshes.length === 0)) {
  //     return 0;
  //   }

  //   const result = GroundUtils.getGroundYFromScene(this.groundMeshes, x, z);
  //   return result?.y ?? null;
  // }


  /**
   * Optionally create a physics collider for a planted tree.
   * No-ops when physics aren't available.
   */
  _addCollider(pos: THREE.Vector3, scale: number, baseHeight: number, baseRadius: number, userData: any) {
    // // const RAPIER = this.RAPIER;
    // const s = scale;
    // const bodyDesc = this.rapier!.RigidBodyDesc.fixed()
    //   .setTranslation(pos.x, pos.y + (baseHeight * s) / 2, pos.z);
    // const body = this.world!.createRigidBody(bodyDesc);
    // const colliderDesc = this.rapier!.ColliderDesc.cylinder(
    //   (baseHeight * s) / 2,
    //   baseRadius * s
    // );
    // const collider = this.world!.createCollider(colliderDesc, body);
    // // @ts-expect-error
    // collider.userData = userData;
    // collider.setCollisionGroups(
    //   (GROUP_OBJECT << 16) | GROUP_BALL
    // );
  }

  #makeBatchMaterial(source: THREE.Material, isBillboardOnly: boolean): THREE.Material {
    const src = source as THREE.MeshStandardMaterial;
    const lowTier = this.qualityLevel === QualityMode.Low;

    if (!lowTier) return source.clone();

    // Standard = same PBR family as Physical, minus clearcoat/sheen/transmission cost
    const cheap = new THREE.MeshStandardMaterial();
    THREE.MeshStandardMaterial.prototype.copy.call(cheap, src);
    return cheap;

    // // Low tier: unlit billboards, cheap-lit foliage/trunks
    // const cheap = isBillboardOnly
    //   ? new THREE.MeshBasicMaterial()
    //   : new THREE.MeshLambertMaterial();
    // cheap.map = src.map;
    // cheap.color.copy(src.color);
    // cheap.side = src.side;
    // cheap.transparent = src.transparent;
    // cheap.alphaTest = src.alphaTest;
    // cheap.depthWrite = src.depthWrite;
    // (cheap as any).alphaToCoverage = (src as any).alphaToCoverage;
    // return cheap;
  }  
  plantFromMask(trees: TreeGroup[], maskData: { data: ImageDataArray, width: number, height: number }, seed = 12345) {
    const { data, width, height } = maskData;
    const cellW = this.worldSize / width;
    const cellH = this.worldSize / height;
    const random = seededRandom(seed);

    const totalDensity = trees.reduce((sum, t) => sum + t.density, 0);

    const cumulativeWeights = [];
    let cumSum = 0;
    for (const t of trees) {
      cumSum += t.density / totalDensity;
      cumulativeWeights.push(cumSum);
    }
    cumulativeWeights[cumulativeWeights.length - 1] = 1.0;

    // Scatter XZ from mask
    const scattered: { x: number, z: number }[][] = trees.map(() => []);
    const sharedGrid = new SpatialHash2D(
      Math.min(...trees.map(t => t.minDistance ?? Infinity)) || 1
    );
    for (let py = 0; py < height; py++) {
      for (let px = 0; px < width; px++) {
        const val = data[(py * width + px) * 4];
        if (val === 0) continue;

        const cellDensity = (val / 255) * totalDensity;
        const count = Math.floor(cellDensity);
        const extra = random() < (cellDensity - count) ? 1 : 0;

        for (let t = 0; t < count + extra; t++) {
          const r = random();
          let treeIdx = 0;
          for (let i = 0; i < cumulativeWeights.length; i++) {
            if (r <= cumulativeWeights[i]) { treeIdx = i; break; }
          }

          const x = (px + random()) * cellW;
          const z = (py + random()) * cellH;

          // ── min-distance check against ALL placed trees ──
          const minDist = trees[treeIdx].minDistance;
          if (minDist) {
            if (sharedGrid.hasNeighborWithin(x, z, minDist)) {
              continue;
            }
          }

          sharedGrid.insert(x, z);  // register for ALL groups to see
          scattered[treeIdx].push({ x, z });
        }
      }
    }

    // Raycast for Y + build matrices per tree type
    const dummy = new THREE.Object3D();
    const allResults = [];

    for (let treeIdx = 0; treeIdx < trees.length; treeIdx++) {
      const { meshGroup, scaleRange, colors, collider: wantColliders } = trees[treeIdx];

      const box = new THREE.Box3().setFromObject(meshGroup);
      const treeSize = box.getSize(new THREE.Vector3());
      const baseHeight = treeSize.y * 0.75;
      const baseRadius = Math.min(treeSize.x, treeSize.z) / 18;

      const points = scattered[treeIdx];
      if (points.length === 0) { allResults.push(null); continue; }

      const matrices: THREE.Matrix4[] = [];
      for (const { x, z } of points) {
        const y = this.#getGroundY(x, z);
        if (y == null) continue;

        dummy.position.set(x, y, z);
        dummy.rotation.set(0, random() * Math.PI * 2, 0);
        const s = scaleRange.min + random() * (scaleRange.max - scaleRange.min);
        dummy.scale.set(s, s, s);
        dummy.updateMatrix();
        matrices.push(dummy.matrix.clone());
      }

      if (matrices.length === 0) { allResults.push(null); continue; }

      // Colliders (only when physics available AND tree config opts in)
      // if (wantColliders) {
      //   const pos = new THREE.Vector3();
      //   const scale = new THREE.Vector3();
      //   const quat = new THREE.Quaternion();

      //   for (let i = 0; i < matrices.length; i++) {
      //     matrices[i].decompose(pos, quat, scale);
      //     this._addCollider(pos, scale.x, baseHeight, baseRadius, {
      //       type: 'tree',
      //       treeIdx,
      //     });
      //   }
      // }


      const count = matrices.length;
      const color = new THREE.Color();
      const pickedColors = colors?.length > 0
        ? Array.from({ length: count }, () => colors[Math.floor(random() * colors.length)])
        : [];

      
      const meshes = this.#buildLODMeshes(trees[treeIdx], matrices, pickedColors, count);
      allResults.push(meshes);
    }
    return allResults;
  }

  #splitByLODLevel(meshGroup: THREE.Group): Map<number, THREE.Group> {
    const levels = new Map<number, THREE.Group>();

    meshGroup.children.forEach((child) => {
      const level = child.userData?.lod ?? 0;
      if (!levels.has(level)) {
        levels.set(level, new THREE.Group());
      }
      levels.get(level)!.add(child.clone());
    });

    for (const group of levels.values()) {
      group.applyMatrix4(meshGroup.matrixWorld);
    }

    return levels;
  }

  #buildLODMeshes(
    treeConfig: TreeGroup,
    matrices: THREE.Matrix4[],
    pickedColors: number[],
    count: number
  ) {
    const { meshGroup, lodDistances } = treeConfig;
    const levels = this.#splitByLODLevel(meshGroup);
    // const color = new THREE.Color();
    const maxLevel = Math.max(...levels.keys());

    // Group submeshes by material across ALL LOD levels.
    // One BatchedMesh per unique material — any texture layout works.
    const groups = new Map<string, { material: THREE.Material, perLevel: THREE.BufferGeometry[][] }>();
    let impostorDef: { material: THREE.Material, level: number, bounds: THREE.Box3 } | null = null;
    for (const [level, sourceGroup] of levels.entries()) {
      for (const child of sourceGroup.children) {
        if (!isMeshObject(child)) continue;

        // Impostor quads bypass batching — they need their own instanced
        // material (metadata travels on material extras → userData)
        const childMat = child.material as THREE.Material;
        if ((childMat.userData as any)?.impostor) {
          const geo = child.geometry.clone();
          child.updateWorldMatrix(true, false);
          const localMatrix = new THREE.Matrix4();
          localMatrix.copy(sourceGroup.matrixWorld).invert().multiply(child.matrixWorld);
          geo.applyMatrix4(localMatrix);
          geo.computeBoundingBox();
          impostorDef = { material: childMat, level, bounds: geo.boundingBox!.clone() };
          geo.dispose();
          continue;
        }

        const geo = child.geometry.clone();
        child.updateWorldMatrix(true, false);
        const localMatrix = new THREE.Matrix4();
        localMatrix.copy(sourceGroup.matrixWorld).invert().multiply(child.matrixWorld);
        geo.applyMatrix4(localMatrix);

        const mat = child.material as THREE.Material;
        let g = groups.get(mat.uuid);
        if (!g) {
          g = { material: mat, perLevel: Array.from({ length: maxLevel + 1 }, () => []) };
          groups.set(mat.uuid, g);
        }
        g.perLevel[level].push(geo);
      }
    }

    /**
     * On the billboard path the geometry batches can never draw.
     *
     * Every instance is pinned to the impostor's level, where these materials
     * have no geometry at all, so each BatchedMesh was merging and uploading
     * LOD0/LOD1 for thousands of trees purely to sit invisible. On a 58 MB
     * course that is GPU memory and load time spent on nothing. Don't build
     * them — but only when there IS an impostor to draw instead, or the trees
     * would have nothing left.
     */
    const skipBatches = this.billboardOnly && impostorDef !== null;

    const batches: BatchEntry[] = [];
    for (const { material, perLevel } of skipBatches ? [] : groups.values()) {
      // Merge same-material submeshes within each LOD level into one geometry
      const lodGeos = perLevel.map(list =>
        list.length === 0 ? null : list.length === 1 ? list[0] : mergeGeometries(list)
      );

      // Any material used at the billboard level gets cutout settings
      const hasBillboard = lodGeos[maxLevel] !== null;
      if (hasBillboard) {
        if (this.qualityLevel === QualityMode.Low) {
          material.alphaTest = 0.6; // no MSAA on Low → A2C unavailable
          (material as any).alphaToCoverage = false;
        } else {
          // alphaTest stays ABOVE zero even with alphaToCoverage on. A2C only
          // does anything when the target is multisampled, and an XR target
          // frequently is not — with a 0.0 threshold the cutout then vanishes
          // entirely and every billboard renders as its full opaque rectangle.
          // Keeping a real threshold makes the cutout MSAA-independent; A2C is
          // left on to soften the edge wherever samples do exist.
          material.alphaTest = 0.4;
          (material as any).alphaToCoverage = true;
        }

        material.transparent = false;
        material.depthWrite = true;
      }

      // Billboard-only = this material has geometry at no other level
      const isBillboardOnly = hasBillboard && lodGeos.filter(Boolean).length === 1;
      const batchMaterial = this.#makeBatchMaterial(material, isBillboardOnly);

      let maxVerts = 0;
      let maxIndices = 0;
      for (const g of lodGeos) {
        if (!g) continue;
        maxVerts += g.attributes.position.count;
        maxIndices += g.index ? g.index.count : g.attributes.position.count;
      }

      // const batched = new THREE.BatchedMesh(count, maxVerts, maxIndices, material);
      // const batched = new THREE.BatchedMesh(count, maxVerts, maxIndices, material.clone());
      const batched = new THREE.BatchedMesh(count, maxVerts, maxIndices, batchMaterial);

      // three.js WebGPU bug: culling+shadows drops opaque batches; revisit on upgrade
      batched.castShadow = false;
      batched.perObjectFrustumCulled = false;
      batched.receiveShadow = false;
      batched.sortObjects = false; // draw-order sort races geometry-ID swaps on WebGPU → one-frame glitches

      // billboardOnly pins every tree to the last level. A batch with no
      // geometry there can never draw a single instance, so it is pure cost:
      // a draw call per eye, every frame, that resolves to nothing. When the
      // impostor quad IS the last level — the usual case on a headset — that
      // silences every geometry batch the tree has.
      if (this.billboardOnly && !hasBillboard) batched.visible = false;

      const lodGeometryIds = lodGeos.map(g => (g ? batched.addGeometry(g) : -1));
      const firstLevel = lodGeometryIds.findIndex(id => id !== -1);

      const instanceIds: number[] = [];
      for (let i = 0; i < count; i++) {
        const id = batched.addInstance(lodGeometryIds[firstLevel]);
        batched.setMatrixAt(id, matrices[i]);
        // Hide instances whose material has no geometry at the starting level (billboard batch)
        if (firstLevel !== 0) batched.setVisibleAt(id, false);
        instanceIds.push(id);
      }

      this.treeGroup.add(batched);
      batches.push({ mesh: batched, lodGeometryIds, instanceIds });
      // BatchedMesh copied the data into its own buffers — free the temporary clones
      for (const g of lodGeos) g?.dispose();

    }

    // The clones were taken before we knew no batch would want them, and
    // nothing else holds a reference — free them rather than leaving a few
    // thousand merged tree geometries pinned for the session.
    if (skipBatches) {
      for (const { perLevel } of groups.values()) {
        for (const list of perLevel) for (const g of list) g.dispose();
      }
    }

    let impostor: ImpostorEntry | undefined;
    if (impostorDef) {
      // impostor = this.#buildImpostor(impostorDef.material, impostorDef.level, matrices, count);
      impostor = this.#buildImpostor(impostorDef, matrices, count);

    }

    const positions = new Float32Array(count * 2);
    const pos = new THREE.Vector3();
    for (let i = 0; i < count; i++) {
      pos.setFromMatrixPosition(matrices[i]);
      positions[i * 2] = pos.x;
      positions[i * 2 + 1] = pos.z;
    }

    const sphere = new THREE.Sphere();
    let cullRadius = 10;
    if (batches.length && batches[0].instanceIds.length) {
      batches[0].mesh.getBoundingSphereAt(batches[0].instanceIds[0], sphere);
      cullRadius = sphere.radius * 1.5; // pad for scale variation across instances
    } else if (impostor) {
      // No batch to measure — the impostor quad's half-extent is the canopy
      // radius by construction, so take the largest planted tree.
      let maxScale = 0;
      for (let i = 0; i < impostor.realScale.length; i++) {
        if (impostor.realScale[i] > maxScale) maxScale = impostor.realScale[i];
      }
      cullRadius = impostor.radius * maxScale * 1.5;
    }
    
    this.lodEntries.push({
      batches,
      impostor,
      allMatrices: matrices,
      positions,
      cullRadius,
      maxDistance: treeConfig.maxDistance ?? Infinity,
      lodDistances,
      currentLevel: new Uint8Array(count),
    });

    return batches.map(b => b.mesh);
  }

  #buildImpostor(
    def: { material: THREE.Material, level: number, bounds: THREE.Box3 },
    matrices: THREE.Matrix4[],
    count: number
  ): ImpostorEntry {
    const { material, level, bounds } = def;

    const meta = (material.userData as any).impostor as ImpostorMeta;
    const map = (material as THREE.MeshStandardMaterial).map!;
    // const center = new THREE.Vector3(...meta.center);
    // Derive size/placement from the quad geometry itself — it went through the
    // same export-scale pipeline as the mesh LODs, unlike the baked metadata.
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const resolved: ImpostorMeta = {
      ...meta,
      radius: Math.max(size.x, size.y) / 2,
      center: [center.x, center.y, center.z],
    };
    const base = new THREE.PlaneGeometry(2, 2); // unit; sized in-shader by radius × scale
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = base.index;
    geo.setAttribute('position', base.attributes.position);
    geo.setAttribute('uv', base.attributes.uv);
    geo.instanceCount = count;

    const posScaleArr = new Float32Array(count * 4); // w starts 0 = hidden
    const yawArr = new Float32Array(count);
    const realScale = new Float32Array(count);
    const p = new THREE.Vector3(), q = new THREE.Quaternion(), s = new THREE.Vector3();
    const e = new THREE.Euler();
    for (let i = 0; i < count; i++) {
      matrices[i].decompose(p, q, s);
      realScale[i] = s.x;
      yawArr[i] = e.setFromQuaternion(q, 'YXZ').y;
      p.copy(center).applyMatrix4(matrices[i]); // quad center in world space
      posScaleArr[i * 4] = p.x;
      posScaleArr[i * 4 + 1] = p.y;
      posScaleArr[i * 4 + 2] = p.z;
    }
    // Debug bisect: every quad up front, at its real size, so nothing about
    // whether they draw depends on the LOD pass running correctly.
    if (this.debugImpostors) {
      for (let i = 0; i < count; i++) posScaleArr[i * 4 + 3] = realScale[i];
    }

    const posScale = new THREE.InstancedBufferAttribute(posScaleArr, 4);
    posScale.setUsage(THREE.DynamicDrawUsage);
    const yaw = new THREE.InstancedBufferAttribute(yawArr, 1);
    geo.setAttribute('iPosScale', posScale);
    geo.setAttribute('iYaw', yaw);

    const { material: mat, camLocal } = createImpostorMaterial(map, resolved, posScale, yaw, {
      qualityLevel: this.qualityLevel,
      debug: this.debugImpostors,
      lightmap: this.lightmap,
      lightmapWorldSize: this.lightmapWorldSize,
    });
    console.log(
      `[plant] impostor: level=${level} count=${count} radius=${resolved.radius.toFixed(2)} `
      + `center=[${resolved.center.map(v => v.toFixed(2)).join(', ')}] `
      + `grid=${meta.grid} map=${(map.image as any)?.width ?? '?'}x${(map.image as any)?.height ?? '?'}`
      + (this.debugImpostors ? ' DEBUG' : ''),
    );
    const mesh = new THREE.Mesh(geo, mat);
    // In-shader world placement → three.js can't cull this correctly per-mesh
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    this.treeGroup.add(mesh);

    return { mesh, posScale, realScale, level, radius: resolved.radius, camLocal };
  }

  static loadTree(tree: THREE.Object3D) {

    const treeGroup = new THREE.Group();
    tree.scale.set(1, 1, 1);
    tree.updateMatrixWorld(true);

    // Find the node that contains the LOD groups
    let lodParent: THREE.Object3D | null = null;
    tree.traverse((child) => {
      if (child.children.some(c => c.userData?.lod_level !== undefined || c.name.match(/^LOD\d+$/))) {
        lodParent = child;
      }
    });

    if (lodParent) {
      // Multi-LOD tree
      for (const lodNode of (lodParent as THREE.Object3D).children) {
        const level = lodNode.userData?.lod_level ?? parseInt(lodNode.name.match(/LOD(\d+)/i)?.[1] ?? '0');

        if (lodNode instanceof THREE.Mesh) {
          const mesh = lodNode.clone();
          lodNode.matrixWorld.decompose(mesh.position, mesh.quaternion, mesh.scale);
          mesh.userData.lod = level;
          treeGroup.add(mesh);
        } else {
          lodNode.traverse((child) => {
            if (child instanceof THREE.Mesh && child.isMesh) {
              const mesh = child.clone();
              child.matrixWorld.decompose(mesh.position, mesh.quaternion, mesh.scale);
              mesh.userData.lod = level;
              treeGroup.add(mesh);
            }
          });
        }
      }
    } else {
      // Single mesh, no LODs — treat everything as LOD0
      tree.traverse((child) => {
        if (child instanceof THREE.Mesh && child.isMesh) {
          const mesh = child.clone();
          child.matrixWorld.decompose(mesh.position, mesh.quaternion, mesh.scale);
          mesh.userData.lod = 0;
          treeGroup.add(mesh);
        }
      });
    }

    // Center at origin
    const box = new THREE.Box3().setFromObject(treeGroup);
    const center = box.getCenter(new THREE.Vector3());
    treeGroup.children.forEach((child) => {
      child.position.x -= center.x;
      child.position.z -= center.z;
      child.position.y -= Math.max(0, box.min.y);  // only shift DOWN if model floats above origin
      // child.position.y -= box.min.y;
    });

    return treeGroup;
  }

  /** Run LOD/culling assignment immediately (call once before first render). */
  primeLODs(camera: THREE.Camera) {
    // Frame 1 draws before update() ever runs, so seed the facing here too or
    // the first frame points every quad at the origin.
    for (const entry of this.lodEntries) {
      entry.impostor?.camLocal.value.copy(camera.position);
    }
    this.#updateLODs(camera);
  }
  
  #updateLODs(camera: THREE.Camera) {
    let changed = 0;
    const camX = camera.position.x;
    const camZ = camera.position.z;
    const HIDDEN = 255;

    const projScreen = this.#projScreen
      .multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    const frustum = this.#frustum.setFromProjectionMatrix(projScreen);
    const sphere = this.#sphere;

    for (const entry of this.lodEntries) {
      // const { batches, positions, lodDistances, maxDistance, currentLevel } = entry;
      const { batches, positions, lodDistances, maxDistance, currentLevel, cullRadius, allMatrices } = entry;

      const count = currentLevel.length;
      // Unused on the billboard-only path, which picks one fixed level.
      const distsSq = this.billboardOnly ? [] : lodDistances.map(d => d * d);
      let impostorDirty = false;
      const maxDistSq = maxDistance * maxDistance;
      const nearCullSq = this.nearCull * this.nearCull;
      const numLevels = batches.length
        ? Math.max(...batches.map(b => b.lodGeometryIds.length))
        : 0;

      /**
       * Frustum culling only pays for itself when a hidden instance means a
       * skipped draw, which is true of BatchedMesh and NOT true of the
       * impostor: its draw call submits every instance every frame regardless,
       * and "hidden" just sets the quad's scale to zero. The GPU was already
       * going to clip an off-screen quad for free.
       *
       * So on the billboard-only path this test bought nothing and cost the
       * pop-in at the edge of vision when you turn your head quickly — the
       * scan is rate-limited to every 10 frames, which at speed is a wider arc
       * than the LOD proxy's margin over the real field of view. Skip it when
       * no batch is drawing and the trees simply stop popping.
       */
      const cullToFrustum = batches.some(b => b.mesh.visible);

      for (let i = 0; i < count; i++) {
        const dx = positions[i * 2] - camX;
        const dz = positions[i * 2 + 1] - camZ;
        const dSq = dx * dx + dz * dz;

        let level = HIDDEN;
        // Too close counts as "not there" just as much as too far — see
        // TreePlanterOptions.nearCull. A tree the player is standing inside has
        // no good rendering.
        if (dSq < maxDistSq && dSq > nearCullSq) {
          if (this.billboardOnly) {
            // Target the impostor's OWN level when there is one, rather than
            // inferring it from the batches. Inferring breaks outright once the
            // batches aren't built at all (numLevels 0 → level 0, which never
            // matches an impostor at level 2, so every quad stays scaled to
            // zero and the trees vanish), and it was always a guess that the
            // billboard happened to be last.
            level = entry.impostor
              ? entry.impostor.level
              : Math.max(numLevels - 1, 0);
          } else {
            level = 0;
            for (let l = 0; l < distsSq.length; l++) {
              if (dSq >= distsSq[l]) level = l + 1;
            }
            level = Math.min(level, numLevels - 1);
          }
        }
        if (level !== HIDDEN && cullToFrustum) {
          sphere.center.setFromMatrixPosition(allMatrices[i]);
          // Pad by an angle, not a fixed distance: the scan is rate-limited, so
          // the frustum has to cover wherever the head can get to before the
          // next one. A tree 200 m out needs far more slack than one at 10 m,
          // and a constant radius gives the distant ones effectively none.
          sphere.radius = cullRadius + Math.sqrt(dSq) * CULL_ANGLE_PAD;
          if (!frustum.intersectsSphere(sphere)) level = HIDDEN;
        }

        if (level === currentLevel[i]) continue;
        currentLevel[i] = level;
        changed++;

        for (const batch of batches) {
          // Permanently hidden batch (see billboardOnly above) — nothing to
          // toggle, and this is the innermost loop over every planted tree.
          if (!batch.mesh.visible) continue;
          const id = batch.instanceIds[i];
          const geoId = level === HIDDEN ? -1 : (batch.lodGeometryIds[level] ?? -1);
          if (geoId === -1) {
            batch.mesh.setVisibleAt(id, false);
          } else {
            batch.mesh.setGeometryIdAt(id, geoId);
            batch.mesh.setVisibleAt(id, true);
          }
        }

        // debugImpostors pins every quad visible at build time; letting the LOD
        // pass write w here would undo that on the first frame and the bisect
        // would report the same "no trees" either way.
        if (entry.impostor && !this.debugImpostors) {
          entry.impostor.posScale.array[i * 4 + 3] =
            level === entry.impostor.level ? entry.impostor.realScale[i] : 0;
          impostorDirty = true;
        }

      }

      // Once per entry, not once per tree: the flag re-uploads the WHOLE
      // instance buffer, so setting it inside the loop paid for the same
      // upload again for every tree that changed level in the same pass.
      if (impostorDirty) entry.impostor!.posScale.needsUpdate = true;
    }
    return changed;

  }
  /**
   * Every planted tree as a trunk cylinder, for collision.
   *
   * Derived from each instance's bounding sphere: a tree's canopy dominates
   * that sphere, so the trunk is taken as a thin fraction of its radius and the
   * strikeable height as most of its diameter. Approximate on purpose — the
   * point is that a ball into the trees drops instead of sailing through, not
   * that it models bark.
   *
   * Call after planting. The result is static, so a course page can build it
   * once at load — which it must, because the ball's collision set is fixed
   * before the first shot and never rebuilt.
   */
  trunks(limit = 6000): TreeTrunk[] {
    const out: TreeTrunk[] = [];
    const sphere = new THREE.Sphere();
    const position = new THREE.Vector3();
    for (const entry of this.lodEntries) {
      const { batches, impostor, allMatrices } = entry;
      // Batches are the better measurement, but they are not built at all on
      // the billboard path — and this is the ball's collision set, so falling
      // through to "no trunks" there would silently let every shot sail
      // through the trees. The impostor's own canopy radius stands in.
      const batch = batches.length ? batches[0] : null;
      if (!batch && !impostor) continue;
      for (let i = 0; i < allMatrices.length && out.length < limit; i++) {
        let canopy: number;
        if (batch) {
          const id = batch.instanceIds[i];
          if (id === undefined) continue;
          batch.mesh.getBoundingSphereAt(id, sphere);
          canopy = sphere.radius;
        } else {
          canopy = impostor!.radius * impostor!.realScale[i];
        }
        position.setFromMatrixPosition(allMatrices[i]);
        if (!Number.isFinite(canopy) || canopy <= 0) continue;
        out.push({
          x: position.x,
          z: position.z,
          radius: Math.max(canopy * 0.10, 0.12),
          base: position.y,
          top: position.y + canopy * 1.7,
        });
      }
    }
    if (out.length >= limit) {
      console.warn(`TreePlanter: trunk colliders capped at ${limit}`);
    }
    return out;
  }

  update(camera: THREE.Camera, isShotActive: boolean) {
    this.#frameNum++;
    this.#framesSinceLOD++;

    // Billboard facing, EVERY frame and before any of the early-outs below.
    // The LOD scan is rate-limited and skipped entirely while the player stands
    // still, but a head that only turns still changes which way every quad has
    // to face — pinning this to the LOD cadence would make the trees snap round
    // in steps as you look about. One Vector3 copy per tree layer.
    for (const entry of this.lodEntries) {
      entry.impostor?.camLocal.value.copy(camera.position);
    }
    // First call: assign LODs immediately so frame 1 never draws
    // every tree at LOD0.
    if (this.#frameNum === 1) {
      this.#updateLODs(camera);
      return;
    }

    // Cheap movement check EVERY frame; rate-limit only the expensive scan.
    const dx = camera.position.x - this.#lastCamX;
    const dz = camera.position.z - this.#lastCamZ;
    const movedSq = dx * dx + dz * dz;
    const dir = camera.getWorldDirection(this.#tmpDir);
    const turned = dir.dot(this.#lastCamDir) < 0.999; // ~2.5° rotation

    // Teleport / camera cut: respond this frame, ignore the rate limit
    const jumped = movedSq > 100; // >10m in one frame

    const minInterval = this.qualityLevel && this.qualityLevel > QualityMode.Low ? 5 : 10;
    if (!jumped) {
      if (this.#framesSinceLOD < minInterval) return;
      if (movedSq < 1.0 && !turned) return; // idle — free
    }

    this.#lastCamDir.copy(dir);
    this.#lastCamX = camera.position.x;
    this.#lastCamZ = camera.position.z;
    this.#framesSinceLOD = 0;
    this.#updateLODs(camera);

  }

  /**
   * Free the hidden template subtrees once planting is complete.
   * Geometries and materials are disposed; textures are NOT (the batch
   * materials still reference them).
   */
  disposeTemplates() {
    const templates: THREE.Object3D[] = [];
    this.scene.traverse((child) => {
      if (child.userData?.type === 'tree_template') templates.push(child);
    });
    for (const t of templates) {
      t.traverse((o: any) => {
        if (o.isMesh) {
          o.geometry?.dispose();
          const mats = Array.isArray(o.material) ? o.material : [o.material];
          for (const m of mats) m?.dispose();  // material only — not m.map
        }
      });
      t.parent?.remove(t);
    }
    console.log(`[mem] disposed ${templates.length} tree templates`);
  }
  
}
